import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
    UsersIcon,
    CubeIcon,
    ArrowPathRoundedSquareIcon,
    WrenchScrewdriverIcon,
    BoltIcon,
    ShieldCheckIcon,
    SparklesIcon,
    ArrowLongRightIcon,
    ArrowLongLeftIcon
} from '@heroicons/react/24/outline';
import { useFirestore } from '../hooks/useFirestore';
import { getServices } from '../firebase/firestore';
import { iconToSlug } from '../data/servicesData';
import Section from '../components/common/Section';
import Card from '../components/common/Card';
import Loading from '../components/common/Loading';
import './Services.css';


const Services = () => {
    const { t, i18n } = useTranslation();
    const { data: services, loading, error } = useFirestore(useCallback(() => getServices(), []));

    const getServiceIcon = (service) => {
        const iconMap = {
            'elevator': UsersIcon,
            'truck': CubeIcon,
            'stairs': ArrowPathRoundedSquareIcon,
            'walk': BoltIcon,
            'wrench': WrenchScrewdriverIcon,
            'refresh': ShieldCheckIcon
        };
        const IconComponent = iconMap[service.icon] || SparklesIcon;
        return <IconComponent className="card-icon-inner" />;
    };

    const isAr = i18n.language === 'ar';
    const ArrowIcon = isAr ? ArrowLongLeftIcon : ArrowLongRightIcon;

    return (
        <div className="services-page">
            <Helmet>
                <title>{t('services.title')} | {t('hero.title')}</title>
                <meta name="description" content={t('services.subtitle')} />
                <html lang={i18n.language} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} />
            </Helmet>

            <main>
                {/* Page Header */}
                <div className="page-header">
                    <div className="container">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-center"
                        >
                            <h1>{t('services.title')}</h1>
                            <div className="accent-line center"></div>
                            <p className="header-subtitle">{t('services.subtitle')}</p>
                        </motion.div>
                    </div>
                </div>

                {/* Services Content */}
                <Section className="services-main-content">
                    {loading && <Loading message={t('common.loading')} />}

                    {error && (
                        <div className="error-container">
                            <p>{t('common.error')}</p>
                        </div>
                    )}

                    {!loading && !error && services && (
                        <div className="grid grid-3">
                            {services.map((service, index) => (
                                <motion.div
                                    key={service.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1, duration: 0.5 }}
                                >
                                    <Card hover={true}>
                                        <div className="card-icon">
                                            {getServiceIcon(service)}
                                        </div>
                                        <h3>{isAr ? service.titleAr : service.titleEn}</h3>
                                        <p>{isAr ? service.descAr : service.descEn}</p>
                                        {iconToSlug[service.icon] && (
                                            <Link
                                                to={`/services/${iconToSlug[service.icon]}`}
                                                className="service-card-link"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--primary-gold)', fontWeight: 600, marginTop: '1rem', fontSize: '0.95rem', transition: 'gap 0.3s' }}
                                            >
                                                {isAr ? 'اعرف المزيد' : 'Learn More'}
                                                <ArrowIcon style={{ width: '1.1rem' }} />
                                            </Link>
                                        )}
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </Section>
            </main>
        </div>
    );
};

export default Services;
